import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { Link } from 'react-router-dom';
import { BulletinMessageModalComp } from '../../components/BulletinMessageModalComp';
import { DocumentHyperlink } from '../../components/DocumentHyperlink';
import { brandContainer, brandIcon, brandText, contentBox, contentBoxSubheadline, contentContainer, contextBoxBody, linkStyle, newlineLinkStyle } from "./styled";
import { WelcomeData } from './WelcomeData';
import { WORKINFO_ROUTES } from './WelcomeModel';

export interface IWelComeProps {
  data: WelcomeData,
  signedIn: boolean,
  onNavigateToCurrentDispatch: () => void
}

/**
 * UI Component - Responsible for UI
 * @returns 
 */
export const Welcome: React.FC<IWelComeProps> = ({ data, signedIn, onNavigateToCurrentDispatch }) => {
  const theme = useTheme();
  return (
    <Box>
      <BulletinMessageModalComp
        bulletins={data.messages || []}
        automatedBulletins={[]}
      />
      <Box sx={brandContainer}>
        <Box sx={brandIcon}>
          <img
            src="assets/images/bcmea_icons/BCMEA-Horizontal-Black-Text.png"
            alt="BC Maritime Employers Association"
            style={{ maxWidth: "100%", maxHeight: "100px" }}
          />
        </Box>
        <Box>
          <Typography sx={brandText} data-automation="app-description">
            Welcome to MyBCMEA, a portal to work information and resources for B.C. ILWU Longshore.
          </Typography>
        </Box>
      </Box>
      <Box sx={contentContainer}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
            <Box sx={contentBox}>
              <Typography sx={contentBoxSubheadline} data-automation="section-header-1">
                New & Improved
              </Typography>
              <Typography sx={contextBoxBody} data-automation="section-body-1">
                MyBCMEA offers the same information and capabilities as before but promises an improved mobile friendly
                experience.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
            <Box sx={contentBox}>
              <Typography sx={contentBoxSubheadline} data-automation="section-header-2">
                Dispatch
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Typography sx={contextBoxBody} data-automation="section-body-2">
                  You can view your Current Dispatch
                  <Typography
                    onClick={onNavigateToCurrentDispatch}
                    sx={{ ...linkStyle, display: 'inline-block', ml: 0.5, textDecoration: 'underline', }}
                  >
                    here
                  </Typography>
                </Typography>
              </Box>
              <Typography sx={contextBoxBody} data-automation="section-body-2">
                You can declare your availability in:
              </Typography>
              {Object.entries(WORKINFO_ROUTES).map(([key, label]) => (
                <DocumentHyperlink
                  link={`/work-info/${key}/all`}
                  key={key}
                  title={label}
                  target='_self'
                  icon={null}
                  dataAutomation={`${key}-work-info-link`}
                  styles={newlineLinkStyle}
                />
              ))}
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
            <Box sx={contentBox}>
              <Typography sx={contentBoxSubheadline} data-automation="section-header-3">
                Support
              </Typography>
              <Typography sx={contextBoxBody} data-automation="section-body-3"
              >
                To get started with using MyBCMEA, here's some helpful information
                <DocumentHyperlink
                  link='/assets/documents/MyBCMEA_HowToDeclare-CheatSheet.pdf'
                  title='Getting Started'
                  target='_blank'
                  icon={null}
                  dataAutomation='user-manual-link'
                  styles={newlineLinkStyle}
                />
                <DocumentHyperlink
                  link='tel:+16042516141'
                  title='604-251-6141'
                  target='_self'
                  icon={null}
                  dataAutomation='telephone-link'
                  styles={newlineLinkStyle}
                />
                <DocumentHyperlink
                  link={`/contact-us${signedIn ? `/signedIn` : ``}`}
                  title='Contact Us'
                  target='_self'
                  icon={null}
                  dataAutomation='contact-us-link'
                  styles={newlineLinkStyle}
                />
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4} xl={3}>
            <Box sx={contentBox}>
              <Typography sx={contentBoxSubheadline} data-automation="section-header-4">
                Resources
              </Typography>
              <Typography sx={contextBoxBody} data-automation="section-body-4">
                For more resources see
                <DocumentHyperlink
                  link='https://www.bcmaritime.com/privacy-policy/'
                  title='Our Privacy Policy'
                  icon={<OpenInNewIcon />}
                  iconOrientation='right'
                  dataAutomation='privacy-policy-link'
                  styles={{
                    ...newlineLinkStyle,
                    '& > .MuiSvgIcon-root': {
                      ml: theme.spacing(0.5),
                      fontSize: 'large',
                      fill: theme.palette.corporateBlue
                    }
                  }}
                />
                <DocumentHyperlink
                  link='/benefits-links'
                  target='_self'
                  title='Links to your Benefits'
                  icon={null}
                  dataAutomation='benefits-link'
                  styles={newlineLinkStyle}
                />
                <DocumentHyperlink
                  link='/faqs'
                  target='_self'
                  title='Frequently Asked Questions'
                  icon={null}
                  dataAutomation='faqs-link'
                  styles={newlineLinkStyle}
                />
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}